import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../../api/httpClient";
import { getRegisterVariant } from "../registerPageConfig";
import { useRegister } from "./useRegister";

/**
 * PG contact invitation flow: prefetch profile-completion, then delegate to useRegister('pgContact').
 */
export function usePgContactRegister() {
  const { token } = useParams();
  const config = useMemo(() => getRegisterVariant("pgContact"), []);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);

  const profileReady = Boolean(profileCompletion) && !profileLoadFailed;
  const includePhoneField = profileReady && !profileCompletion?.phone_number;

  const reg = useRegister("pgContact", {
    tokenFromRoute: token,
    includePhoneField,
  });

  const fetchProfileCompletion = useCallback(
    async (t) => {
      if (!t) {
        setProfileCompletion(null);
        setProfileLoadFailed(true);
        setLoadingProfile(false);
        return;
      }
      setLoadingProfile(true);
      setProfileLoadFailed(false);
      try {
        const response = await apiClient(config.profileCompletionPath(t), "GET");
        setProfileCompletion(response);
        setProfileLoadFailed(false);
      } catch {
        setProfileCompletion(null);
        setProfileLoadFailed(true);
        toast.error("Failed to load profile information. Please try again.");
      } finally {
        setLoadingProfile(false);
      }
    },
    [config]
  );

  useEffect(() => {
    fetchProfileCompletion(token);
  }, [token, fetchProfileCompletion]);

  const retryProfileLoad = useCallback(() => {
    void fetchProfileCompletion(token);
  }, [fetchProfileCompletion, token]);

  return {
    ...reg,
    loadingProfile,
    profileCompletion,
    profileLoadFailed: profileLoadFailed || !token,
    retryProfileLoad,
    /** Show masked phone column when profile still needs phone. */
    showPgPhoneMask: includePhoneField,
  };
}
